import React, { useMemo } from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { Offer } from 'common/contracts';
import { DATE_TIME_FORMAT } from 'constants/constants';
import { SortingOffersValue } from 'constants/enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import SortableHeader from 'components/SortableHeader/SortableHeader';

import { IOfferColumnsBaseProps } from '../../../Offer.types';
import {
  getScheduleDisplayValue,
  renderOfferStatusChip
} from '../../../Offer.utils';

const dayjsUTC = dayjs.extend(utc);

interface IUseRollingOffersColumnsProps extends IOfferColumnsBaseProps {
  hasFeatureFlagCheats: boolean;
  onOpenCheats: (offerId: string) => void;
}

interface IUseRollingOffersColumnsReturn {
  columns: ColumnDef<Offer>[];
}

/**
 * Hook that builds the column definitions for the Rolling Offers table.
 *
 * @example
 * const { columns } = useRollingOffersColumns({
 *   sorting, updateSorting, isLoading, actions, permissions,
 *   hasFeatureFlagCheats, onOpenCheats
 * });
 */
export const useRollingOffersColumns = ({
  sorting,
  updateSorting,
  isLoading,
  actions,
  permissions,
  hasFeatureFlagCheats,
  onOpenCheats
}: IUseRollingOffersColumnsProps): IUseRollingOffersColumnsReturn => {
  const columns = useMemo((): ColumnDef<Offer>[] => {
    return [
      {
        accessorKey: 'offerId',
        header: () => (
          <SortableHeader
            label="ID"
            sortValue={SortingOffersValue.ID}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'publisherOfferId',
        header: () => (
          <SortableHeader
            label="External ID"
            sortValue={SortingOffersValue.PUBLISHER_OFFER_ID}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'name',
        header: () => (
          <SortableHeader
            label="Name"
            sortValue={SortingOffersValue.NAME}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'subOffers',
        header: 'Sub Offers',
        cell: ({ row }: any) => row.original.productsSequence?.length || 0
      },
      {
        accessorKey: 'schedule',
        header: () => (
          <SortableHeader
            label="Schedule"
            sortValue={SortingOffersValue.SCHEDULE}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ row }: any) => {
          return (
            <span className="whitespace-nowrap">
              {getScheduleDisplayValue(row.original)}
            </span>
          );
        }
      },
      {
        accessorKey: 'price',
        header: 'Price',
        cell: () => '-'
      },
      {
        accessorKey: 'active',
        header: () => (
          <SortableHeader
            label="Status"
            sortValue={SortingOffersValue.STATUS}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ row }: any) => {
          return renderOfferStatusChip(row.original);
        }
      },
      {
        accessorKey: 'createdAt',
        header: () => (
          <SortableHeader
            label="Created At"
            sortValue={SortingOffersValue.CREATED_AT}
            currentSortValue={sorting.sortValue}
            currentDirection={sorting.direction}
            onSort={updateSorting}
          />
        ),
        cell: ({ row }: any) => {
          if (isLoading) {
            return '';
          }
          return (
            <span className="whitespace-nowrap">
              {dayjsUTC.utc(row.original.createdAt).format(DATE_TIME_FORMAT)}
            </span>
          );
        }
      },
      {
        accessorKey: 'actions',
        header: '',
        maxSize: 60,
        minSize: 60,
        size: 60,
        cell: ({ row }: any) => (
          <div className="max-w-[60px] w-[60px]">
            <ActionCell
              rowData={row.original}
              actions={[
                ...(hasFeatureFlagCheats
                  ? [
                      {
                        label: 'Cheats',
                        handler: () => {
                          onOpenCheats(row.original.offerId);
                        },
                        disabled:
                          !permissions.canUserEdit ||
                          !permissions.canManageOffers
                      }
                    ]
                  : []),
                {
                  label: 'Edit',
                  handler: () => {
                    actions.onEdit(row.original.offerId);
                  },
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers,
                  hidden: !permissions.canManageOffers
                },
                {
                  label: 'Edit',
                  handler: () => {
                    actions.onLimitedEdit(row.original.offerId);
                  },
                  disabled:
                    !permissions.canUserEdit ||
                    !permissions.canUpdateStatusDesign,
                  hidden:
                    !permissions.canUpdateStatusDesign ||
                    permissions.canManageOffers
                },
                {
                  label: row.original.active ? 'Deactivate' : 'Activate',
                  handler: () =>
                    actions.onToggleStatus(
                      row.original.offerId,
                      row.original.active
                    ),
                  disabled: !permissions.canUserEdit
                },
                {
                  label: 'Duplicate',
                  handler: () => actions.onDuplicate(row.original.offerId),
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers
                },
                {
                  label: 'Delete',
                  handler: () => actions.onDelete(row.original.offerId),
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers
                }
              ]}
            />
          </div>
        )
      }
    ];
  }, [
    sorting,
    updateSorting,
    isLoading,
    actions,
    permissions,
    hasFeatureFlagCheats,
    onOpenCheats
  ]);

  return { columns };
};
